import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, RotateCcw, Moon, Sun } from 'lucide-react';
import { numberToChinese } from '../../../utils/numberToChinese';

interface ChapterPreviewModalProps {
  /** 作品名称（顶部信息区显示） */
  bookTitle: string;
  /** 章节序号（用于「第 N 章」标题） */
  chapterNumber: number;
  /** 章节标题（编辑器当前值，含未保存修改） */
  title: string;
  /** 章节内容 HTML（编辑器当前值，含未保存修改） */
  content: string;
  onClose: () => void;
}

/** 与原阅读模式一致的 XSS 过滤（移除脚本/事件属性等） */
function sanitizeHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html || '', 'text/html');
  doc.querySelectorAll('script, style, iframe, object, embed, link, meta').forEach((el) => el.remove());
  doc.querySelectorAll('*').forEach((el) => {
    Array.from(el.attributes).forEach((attr) => {
      if (/^on/i.test(attr.name)) el.removeAttribute(attr.name);
    });
  });
  return doc.body.innerHTML;
}

// ── 预览设置（localStorage 持久化） ───────────────────────────────

interface PreviewSettings {
  /** 背景色（夜间模式关闭时生效） */
  bg: string;
  /** 夜间模式 */
  night: boolean;
  /** 正文字号（px） */
  fontSize: number;
  /** 字间距（em） */
  letterSpacing: number;
}

const STORAGE_KEY = 'xts_preview_settings';

const DEFAULT_SETTINGS: PreviewSettings = {
  bg: '#f7f3ec',
  night: false,
  fontSize: 17,
  letterSpacing: 0,
};

const BG_OPTIONS = ['#f7f3ec', '#ffffff', '#f5ecd7', '#e7efe4', '#e7eef6', '#eee8d8'];
const LS_OPTIONS = [0, 0.02, 0.04, 0.06, 0.08];
const FONT_MIN = 14;
const FONT_MAX = 22;

function loadSettings(): PreviewSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(raw) as Partial<PreviewSettings>;
    return {
      bg: typeof parsed.bg === 'string' ? parsed.bg : DEFAULT_SETTINGS.bg,
      night: typeof parsed.night === 'boolean' ? parsed.night : DEFAULT_SETTINGS.night,
      fontSize: typeof parsed.fontSize === 'number'
        ? Math.min(FONT_MAX, Math.max(FONT_MIN, Math.round(parsed.fontSize)))
        : DEFAULT_SETTINGS.fontSize,
      letterSpacing: typeof parsed.letterSpacing === 'number' && LS_OPTIONS.includes(parsed.letterSpacing)
        ? parsed.letterSpacing
        : DEFAULT_SETTINGS.letterSpacing,
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

function persistSettings(s: PreviewSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch { /* 存储不可用时静默 */ }
}

// ── 主题配色（亮色 / 夜间） ───────────────────────────────────────

function themeOf(s: PreviewSettings) {
  if (s.night) {
    return {
      bg: '#1b1a18',
      text: '#c9c4bb',
      heading: '#e4dfd6',
      secondary: '#8f8a80',
      topbar: '#b5b0a7',
      quoteBorder: '#4a463f',
      quoteText: '#8f8a80',
      hr: '#3a372f',
      homeBar: 'rgba(255, 255, 255, 0.35)',
    };
  }
  return {
    bg: s.bg,
    text: '#24262b',
    heading: '#1c1e22',
    secondary: '#6f6a60',
    topbar: '#47433c',
    quoteBorder: '#c9c2b5',
    quoteText: '#77726a',
    hr: '#d8d2c6',
    homeBar: 'rgba(20, 22, 26, 0.45)',
  };
}

export function ChapterPreviewModal({ bookTitle, chapterNumber, title, content, onClose }: ChapterPreviewModalProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);
  const [settings, setSettings] = useState<PreviewSettings>(loadSettings);

  const updateSettings = (patch: Partial<PreviewSettings>) => {
    setSettings(prev => {
      const next = { ...prev, ...patch };
      persistSettings(next);
      return next;
    });
  };

  const resetSettings = () => {
    setSettings({ ...DEFAULT_SETTINGS });
    persistSettings({ ...DEFAULT_SETTINGS });
  };

  // Escape 关闭
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // 滚动进度：当前章节已读百分比
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const max = el.scrollHeight - el.clientHeight;
        setProgress(max > 0 ? Math.min(1, Math.max(0, el.scrollTop / max)) : 0);
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // 内容变化（编辑器继续输入）时重置阅读位置
  useEffect(() => {
    setProgress(0);
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [content]);

  const safeHtml = useMemo(() => sanitizeHtml(content), [content]);
  const pct = Math.round(progress * 100);
  const theme = themeOf(settings);
  const lsIndex = LS_OPTIONS.indexOf(settings.letterSpacing);

  const phoneVars = {
    '--pv-bg': theme.bg,
    '--pv-text': theme.text,
    '--pv-heading': theme.heading,
    '--pv-secondary': theme.secondary,
    '--pv-topbar': theme.topbar,
    '--pv-quote-border': theme.quoteBorder,
    '--pv-quote-text': theme.quoteText,
    '--pv-hr': theme.hr,
    '--pv-home': theme.homeBar,
  } as React.CSSProperties;

  return (
    <div className="cm-preview-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      {/* 关闭按钮（编辑区域右上角） */}
      <button className="cm-preview-close" onClick={onClose} title="关闭预览 (Esc)">
        <X size={14} />
      </button>

      <div className="cm-preview-stage">
        {/* iPhone 外壳 */}
        <div className="cm-preview-phone" style={phoneVars} role="dialog" aria-label="手机预览">
          {/* 灵动岛 */}
          <div className="cm-preview-notch" />

          {/* 手机屏幕内容：章节阅读效果 */}
          <div className="cm-preview-screen">
            {/* 顶部信息区：与灵动岛同一行（前置摄像头行）——左侧作品名，右侧章节信息 */}
            <div className="cm-preview-topbar">
              <span className="cm-preview-bookinfo cm-preview-bookinfo--left" title={bookTitle || '未命名作品'}>
                {bookTitle || '未命名作品'}
              </span>
              <span className="cm-preview-bookinfo cm-preview-bookinfo--right" title={`第${numberToChinese(chapterNumber)}章 ${title}`}>
                第{numberToChinese(chapterNumber)}章 {title || '未命名章节'}
              </span>
            </div>

            <div className="cm-preview-content" ref={contentRef}>
              <h1 className="cm-preview-heading">
                第{numberToChinese(chapterNumber)}章 {title || '未命名章节'}
              </h1>
              {safeHtml ? (
                <div
                  className="cm-preview-body"
                  style={{ fontSize: `${settings.fontSize}px`, letterSpacing: `${settings.letterSpacing}em` }}
                  dangerouslySetInnerHTML={{ __html: safeHtml }}
                />
              ) : (
                <p className="cm-preview-empty">本章暂无内容</p>
              )}
            </div>

            {/* 底部渐隐层：手势条及其下方不显示正文 */}
            <div className="cm-preview-bottom-fade" />

            {/* 底部左下角：当前章节阅读进度 */}
            <span className="cm-preview-progress-label">已读 {pct}%</span>

            {/* Home 指示条 */}
            <div className="cm-preview-home-bar" />
          </div>
        </div>

        {/* 右侧：预览设置面板 */}
        <div className="cm-preview-panel">
          <div className="cm-preview-panel-title">预览设置</div>

          {/* 背景色 */}
          <div className="cm-preview-setting">
            <div className="cm-preview-setting-label">背景色</div>
            <div className={`cm-preview-swatches${settings.night ? ' is-disabled' : ''}`}>
              {BG_OPTIONS.map(bg => (
                <button
                  key={bg}
                  className={`cm-preview-swatch${!settings.night && settings.bg === bg ? ' is-active' : ''}`}
                  style={{ background: bg }}
                  title={bg}
                  disabled={settings.night}
                  onClick={() => updateSettings({ bg })}
                />
              ))}
            </div>
          </div>

          {/* 夜间模式 */}
          <div className="cm-preview-setting-row">
            <span className="cm-preview-setting-label">夜间模式</span>
            <button
              className={`cm-preview-switch${settings.night ? ' is-on' : ''}`}
              onClick={() => updateSettings({ night: !settings.night })}
              role="switch"
              aria-checked={settings.night}
              title={settings.night ? '切换为日间模式' : '切换为夜间模式'}
            >
              <span className="cm-preview-switch-thumb" />
            </button>
          </div>

          {/* 字体大小 */}
          <div className="cm-preview-setting">
            <div className="cm-preview-setting-label">字体大小</div>
            <div className="cm-preview-stepper">
              <button disabled={settings.fontSize <= FONT_MIN} onClick={() => updateSettings({ fontSize: settings.fontSize - 1 })}>−</button>
              <span className="cm-preview-stepper-value">{settings.fontSize}</span>
              <button disabled={settings.fontSize >= FONT_MAX} onClick={() => updateSettings({ fontSize: settings.fontSize + 1 })}>+</button>
            </div>
          </div>

          {/* 字间距 */}
          <div className="cm-preview-setting">
            <div className="cm-preview-setting-label">字间距</div>
            <div className="cm-preview-stepper">
              <button disabled={lsIndex <= 0} onClick={() => updateSettings({ letterSpacing: LS_OPTIONS[lsIndex - 1] })}>−</button>
              <span className="cm-preview-stepper-value">{settings.letterSpacing === 0 ? '标准' : `${settings.letterSpacing}em`}</span>
              <button disabled={lsIndex >= LS_OPTIONS.length - 1} onClick={() => updateSettings({ letterSpacing: LS_OPTIONS[lsIndex + 1] })}>+</button>
            </div>
          </div>

          {/* 重置 */}
          <button className="cm-preview-reset" onClick={resetSettings}>
            <RotateCcw size={13} />
            <span>重置设置</span>
          </button>

          <div className="cm-preview-panel-note">
            {settings.night ? <Moon size={11} /> : <Sun size={11} />}
            <span>设置保存在浏览器缓存中</span>
          </div>
        </div>
      </div>
    </div>
  );
}
